/**
 * CuratedResourceRenderer — learner-facing renderer for curated_resource segments.
 *
 * Renders an external article, online course, YouTube video, or document as a
 * rich resource card. Auto-detects embeddable URLs (YouTube, Google Slides) and
 * renders an inline iframe; all other URLs fall back to a thumbnail image or a
 * generic link-preview card.
 *
 * Props:
 *   segment   — the segment object from TutorialPlayer (segment_type === 'curated_resource')
 *   onComplete — optional callback; called when the learner clicks "Mark as Complete"
 */
import React, { useEffect, useRef, useState } from 'react';

/** Heavy third-party players — auto-embedding these often freezes the tab on unmount. */
const HEAVY_EMBED_KINDS = new Set(['youtube', 'vimeo', 'loom']);

function unloadIframe(el) {
  if (!el) return;
  try {
    el.src = 'about:blank';
  } catch { /* ignore */ }
  try {
    el.removeAttribute('src');
  } catch { /* ignore */ }
}

// ─── embed detection ────────────────────────────────────────────────────────

function classifyResourceUrl(url) {
  if (!url || typeof url !== 'string') return null;

  // YouTube
  const yt = url.match(
    /(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([A-Za-z0-9_-]{6,})/,
  );
  if (yt) {
    return { kind: 'youtube', embedUrl: `https://www.youtube.com/embed/${yt[1]}` };
  }

  // Google Slides — convert /pub or /edit URLs to /embed
  const gs = url.match(/docs\.google\.com\/presentation\/d\/([A-Za-z0-9_-]+)/);
  if (gs) {
    return {
      kind: 'google_slides',
      embedUrl: `https://docs.google.com/presentation/d/${gs[1]}/embed?start=false&loop=false`,
    };
  }

  // Google Docs
  const gd = url.match(/docs\.google\.com\/document\/d\/([A-Za-z0-9_-]+)/);
  if (gd) {
    return {
      kind: 'google_docs',
      embedUrl: `https://docs.google.com/document/d/${gd[1]}/preview`,
    };
  }

  // Google Sheets
  const gsh = url.match(/docs\.google\.com\/spreadsheets\/d\/([A-Za-z0-9_-]+)/);
  if (gsh) {
    return {
      kind: 'google_sheets',
      embedUrl: `https://docs.google.com/spreadsheets/d/${gsh[1]}/preview`,
    };
  }

  // Vimeo
  const vm = url.match(/vimeo\.com\/(?:video\/)?(\d{5,})/);
  if (vm) {
    return { kind: 'vimeo', embedUrl: `https://player.vimeo.com/video/${vm[1]}` };
  }

  // Loom
  const loom = url.match(/loom\.com\/share\/([A-Za-z0-9]+)/);
  if (loom) {
    return { kind: 'loom', embedUrl: `https://www.loom.com/embed/${loom[1]}` };
  }

  // CodePen
  const cp = url.match(/codepen\.io\/([^/]+)\/(?:pen|full)\/([A-Za-z0-9]+)/);
  if (cp) {
    return { kind: 'codepen', embedUrl: `https://codepen.io/${cp[1]}/embed/${cp[2]}?default-tab=result` };
  }

  // Notion public pages
  const notion = url.match(/notion\.site\/([A-Za-z0-9-]+)/);
  if (notion) {
    return { kind: 'notion', embedUrl: url };
  }

  // Figma
  const figma = url.match(/figma\.com\/(file|proto|design)\/([A-Za-z0-9]+)/);
  if (figma) {
    return { kind: 'figma', embedUrl: `https://www.figma.com/embed?embed_host=teachiq&url=${encodeURIComponent(url)}` };
  }

  // Canva
  if (url.includes('canva.com/design/')) {
    return { kind: 'canva', embedUrl: url.replace(/\?.*$/, '') + '?embed' };
  }

  // Most external sites block iframe embedding. Only try for known-safe domains.
  const NON_EMBEDDABLE = [
    'coursera.org', 'udemy.com', 'edx.org', 'linkedin.com',
    'mckinsey.com', 'hbr.org', 'medium.com', 'substack.com',
    'a16z.com', 'andreessen', 'techcrunch.com', 'wired.com',
    'nytimes.com', 'wsj.com', 'ft.com', 'bloomberg.com',
    'arxiv.org', 'nature.com', 'sciencedirect.com',
    'github.com', 'github.io', 'stackoverflow.com', 'twitter.com', 'x.com',
    'amazon.com', 'reddit.com', 'facebook.com', 'instagram.com',
    'bbc.com', 'cnn.com', 'theguardian.com', 'forbes.com',
    'tiktok.com', 'apple.com', 'microsoft.com', 'google.com',
    'openai.com', 'anthropic.com', 'deepmind.com',
    'langchain.com', 'langchain-ai.github.io',
  ];
  try {
    const host = new URL(url).hostname.replace(/^www\./, '');
    if (NON_EMBEDDABLE.some((d) => host === d || host.endsWith('.' + d))) {
      return null;
    }
  } catch { /* invalid URL — don't embed */ return null; }

  return { kind: 'tryEmbed', embedUrl: url };
}

// ─── component ──────────────────────────────────────────────────────────────

export default function CuratedResourceRenderer({ segment, onComplete }) {
  const [completed, setCompleted] = useState(false);
  const [embedFailed, setEmbedFailed] = useState(false);
  const iframeRef = useRef(null);

  const resourceUrl = segment.resource_url || segment.scene_data?.resource_url || '';
  const title =
    segment.title ||
    segment.scene_data?.title ||
    segment.script_text ||
    'External Resource';
  const description =
    segment.description ||
    segment.scene_data?.description ||
    segment.script_text ||
    '';
  const sourceName =
    segment.source_name || segment.scene_data?.source_name || '';
  const estimatedMinutes =
    segment.estimated_minutes ?? segment.scene_data?.estimated_minutes ?? null;
  const thumbnailUrl =
    segment.thumbnail_url || segment.scene_data?.thumbnail_url || '';
  const learningObjectives = Array.isArray(segment.learning_objectives)
    ? segment.learning_objectives
    : Array.isArray(segment.scene_data?.learning_objectives)
    ? segment.scene_data.learning_objectives
    : [];

  const embed = resourceUrl ? classifyResourceUrl(resourceUrl) : null;
  // YouTube/Vimeo/Loom start collapsed — auto-embed + unmount was freezing the
  // lesson player for 30s+ when advancing (Hova W3 curated resource report).
  const [viewMode, setViewMode] = useState(
    () => (embed && HEAVY_EMBED_KINDS.has(embed.kind) ? 'external' : 'embed'),
  ); // 'embed' | 'external'
  const isKnownEmbeddable = embed && embed.kind !== 'tryEmbed';
  const shouldTryEmbed = embed && embed.kind === 'tryEmbed' && !embedFailed;
  const showEmbed = (isKnownEmbeddable || shouldTryEmbed) && viewMode === 'embed';

  // Soft-unload the player before React tears the tree down.
  useEffect(() => () => unloadIframe(iframeRef.current), []);

  // Parent navigates away — blank the iframe immediately (see data-curated-embed).
  useEffect(() => {
    const onUnload = () => unloadIframe(iframeRef.current);
    window.addEventListener('teachiq:unload-curated-embeds', onUnload);
    return () => window.removeEventListener('teachiq:unload-curated-embeds', onUnload);
  }, []);

  const handleIframeError = () => {
    if (embed?.kind === 'tryEmbed') setEmbedFailed(true);
  };

  const handleMarkComplete = () => {
    setCompleted(true);
    // Collapse heavy embeds before advancing so goNext does not fight YouTube teardown.
    if (showEmbed) {
      unloadIframe(iframeRef.current);
      setViewMode('external');
    }
    if (!onComplete) return;
    window.setTimeout(() => onComplete(), showEmbed ? 60 : 0);
  };

  let hostname = '';
  try { hostname = new URL(resourceUrl).hostname.replace(/^www\./, ''); } catch {}

  if (!resourceUrl) {
    return (
      <div style={placeholderStyle}>
        <div style={{ fontSize: 32, marginBottom: 8 }}>🔗</div>
        <div style={{ fontWeight: 600, marginBottom: 4 }}>No resource URL set</div>
        <div style={{ fontSize: 13, color: '#94a3b8' }}>
          A coach needs to add a <code>resource_url</code> to this segment.
        </div>
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      {/* ── view mode toggle (when embedded) ── */}
      {showEmbed && (
        <div style={viewToggleBarStyle}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 13, color: '#64748b' }}>
              Viewing on {hostname || 'external site'}
            </span>
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <button
              onClick={() => {
                unloadIframe(iframeRef.current);
                setViewMode('external');
              }}
              style={toggleBtnStyle(false)}
              title="Show resource card instead"
            >
              Collapse
            </button>
            <a
              href={resourceUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{ ...toggleBtnStyle(false), textDecoration: 'none' }}
              title="Open in new tab"
            >
              Open in new tab ↗
            </a>
          </div>
        </div>
      )}

      {/* ── embed / thumbnail preview ── */}
      {showEmbed ? (
        <div style={iframeWrapStyle} data-curated-embed={embed?.kind || 'embed'}>
          <iframe
            ref={iframeRef}
            src={embed.embedUrl}
            title={title}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            style={iframeStyle}
            sandbox="allow-scripts allow-same-origin allow-popups allow-forms"
            onError={handleIframeError}
            onLoad={(e) => {
              try {
                // If the iframe loaded but the site blocked it, contentDocument
                // access will throw. We catch and fall back gracefully.
                const doc = e.target.contentDocument;
                if (doc && doc.body && doc.body.innerHTML === '') handleIframeError();
              } catch {
                // cross-origin — expected for most embeds, not an error
              }
            }}
          />
        </div>
      ) : thumbnailUrl && viewMode !== 'embed' ? (
        <img
          src={thumbnailUrl}
          alt={title}
          style={thumbnailStyle}
          onError={(e) => { e.currentTarget.style.display = 'none'; }}
        />
      ) : viewMode === 'embed' && !showEmbed ? (
        /* embed was attempted but failed, or non-embeddable — show link banner */
        <a
          href={resourceUrl}
          target="_blank"
          rel="noopener noreferrer"
          style={linkBannerAnchorStyle}
        >
          <div style={linkIconBannerStyle}>
            <span style={{ fontSize: 36 }}>📖</span>
            <span style={linkBannerTitleStyle}>{title}</span>
            <span style={linkBannerDomainStyle}>{hostname}</span>
          </div>
        </a>
      ) : null}

      {/* ── card body ── */}
      <div style={cardBodyStyle}>
        {/* source + time meta */}
        {(sourceName || estimatedMinutes !== null) && (
          <div style={metaRowStyle}>
            {sourceName && (
              <span style={sourceChipStyle}>{sourceName}</span>
            )}
            {estimatedMinutes !== null && (
              <span style={timeChipStyle}>
                ⏱ {estimatedMinutes} min
              </span>
            )}
          </div>
        )}

        {/* title */}
        <h3 style={titleStyle}>{title}</h3>

        {/* description — hide internal-facing "Curated ..." labels */}
        {description && description !== title && !description.startsWith('Curated ') && (
          <p style={descStyle}>{description}</p>
        )}

        {/* learning objectives */}
        {learningObjectives.length > 0 && (
          <div style={objectivesBoxStyle}>
            <div style={objectivesLabelStyle}>Learning objectives</div>
            <ul style={objectivesListStyle}>
              {learningObjectives.map((obj, i) => (
                <li key={i} style={objectiveItemStyle}>{obj}</li>
              ))}
            </ul>
          </div>
        )}

        {/* action buttons */}
        <div style={actionRowStyle}>
          {!showEmbed && isKnownEmbeddable ? (
            <button
              onClick={() => { setViewMode('embed'); setEmbedFailed(false); }}
              style={openBtnStyle}
            >
              {HEAVY_EMBED_KINDS.has(embed?.kind) ? 'Watch Inline ↓' : 'View Inline ↓'}
            </button>
          ) : !showEmbed && !embedFailed && embed?.kind === 'tryEmbed' ? (
            <button
              onClick={() => { setViewMode('embed'); setEmbedFailed(false); }}
              style={openBtnStyle}
            >
              View Inline ↓
            </button>
          ) : null}
          <a
            href={resourceUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={showEmbed ? secondaryOpenBtnStyle : openBtnStyle}
          >
            {showEmbed ? 'Open in new tab ↗' : 'Open Resource →'}
          </a>
          <button
            onClick={handleMarkComplete}
            disabled={completed}
            style={completeBtnStyle(completed)}
          >
            {completed ? '✓ Marked Complete' : 'Mark as Complete'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── styles ─────────────────────────────────────────────────────────────────

const containerStyle = {
  border: '1px solid #e2e8f0',
  borderRadius: 12,
  overflow: 'hidden',
  background: '#fff',
  fontFamily: 'system-ui, -apple-system, sans-serif',
};

const iframeWrapStyle = {
  position: 'relative',
  paddingBottom: '56.25%',
  height: 0,
  background: '#000',
  overflow: 'hidden',
};

const iframeStyle = {
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  border: 0,
};

const thumbnailStyle = {
  width: '100%',
  maxHeight: 240,
  objectFit: 'cover',
  display: 'block',
};

const linkBannerAnchorStyle = {
  textDecoration: 'none',
  display: 'block',
};

const linkIconBannerStyle = {
  background: 'linear-gradient(135deg, #0f172a 0%, #1e3a5f 100%)',
  padding: '32px 24px',
  textAlign: 'center',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 8,
};

const linkBannerTitleStyle = {
  color: '#e2e8f0',
  fontSize: 16,
  fontWeight: 600,
  lineHeight: 1.4,
  maxWidth: 400,
};

const linkBannerDomainStyle = {
  color: '#64748b',
  fontSize: 12,
  letterSpacing: 0.3,
};

const placeholderStyle = {
  background: '#f1f5f9',
  borderRadius: 10,
  padding: 32,
  textAlign: 'center',
  color: '#64748b',
};

const cardBodyStyle = {
  padding: '20px 24px 24px',
};

const metaRowStyle = {
  display: 'flex',
  gap: 8,
  alignItems: 'center',
  flexWrap: 'wrap',
  marginBottom: 10,
};

const sourceChipStyle = {
  background: '#dbeafe',
  color: '#1e40af',
  fontSize: 11,
  fontWeight: 700,
  padding: '2px 8px',
  borderRadius: 999,
  letterSpacing: 0.3,
};

const timeChipStyle = {
  background: '#f1f5f9',
  color: '#475569',
  fontSize: 12,
  padding: '2px 8px',
  borderRadius: 999,
};

const titleStyle = {
  margin: '0 0 10px',
  fontSize: 18,
  fontWeight: 700,
  color: '#0f172a',
  lineHeight: 1.3,
};

const descStyle = {
  fontSize: 14,
  lineHeight: 1.65,
  color: '#374151',
  margin: '0 0 16px',
  whiteSpace: 'pre-wrap',
};

const objectivesBoxStyle = {
  background: '#f8fafc',
  border: '1px solid #e2e8f0',
  borderRadius: 8,
  padding: '12px 16px',
  marginBottom: 20,
};

const objectivesLabelStyle = {
  fontSize: 11,
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: 0.8,
  color: '#64748b',
  marginBottom: 8,
};

const objectivesListStyle = {
  margin: 0,
  paddingLeft: 18,
};

const objectiveItemStyle = {
  fontSize: 13.5,
  lineHeight: 1.6,
  color: '#1e293b',
  marginBottom: 4,
};

const actionRowStyle = {
  display: 'flex',
  gap: 10,
  flexWrap: 'wrap',
  alignItems: 'center',
  marginTop: 4,
};

const viewToggleBarStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '8px 16px',
  background: '#f8fafc',
  borderBottom: '1px solid #e2e8f0',
};

const toggleBtnStyle = () => ({
  background: '#fff',
  border: '1px solid #d1d5db',
  color: '#374151',
  padding: '4px 12px',
  borderRadius: 6,
  fontSize: 12,
  fontWeight: 600,
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
});

const openBtnStyle = {
  display: 'inline-block',
  background: '#3b82f6',
  color: '#fff',
  padding: '10px 20px',
  borderRadius: 7,
  fontSize: 14,
  fontWeight: 700,
  textDecoration: 'none',
  cursor: 'pointer',
  lineHeight: 1,
  border: 'none',
};

const secondaryOpenBtnStyle = {
  ...openBtnStyle,
  background: '#fff',
  color: '#3b82f6',
  border: '1px solid #3b82f6',
};

const completeBtnStyle = (done) => ({
  background: done ? '#dcfce7' : '#fff',
  color: done ? '#166534' : '#374151',
  border: `1px solid ${done ? '#86efac' : '#d1d5db'}`,
  padding: '10px 16px',
  borderRadius: 7,
  fontSize: 14,
  fontWeight: 600,
  cursor: done ? 'default' : 'pointer',
});
